import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {Page} from '@/lib/page'
import type {components} from '@/lib/api-schema'

export type Usuario = components['schemas']['AdminUserRead']
export type Membresia = components['schemas']['MembresiaRead']

export const USUARIOS_PAGE_SIZE = 20

/** Los filtros del servidor son combinables, pero la barra de chips es de uno a la vez. */
export type FiltroUsuarios = 'todos' | 'sin_correo' | 'sin_verificar' | 'sin_acceso'

const KEY = ['admin-usuarios']

export function useUsuarios(params: {page: number; search: string; filtro: FiltroUsuarios}, enabled = true) {
  return useQuery({
    queryKey: [...KEY, params],
    queryFn: async () =>
      (
        await api.get<Page<Usuario>>('/admin/users', {
          params: {
            page: params.page,
            page_size: USUARIOS_PAGE_SIZE,
            search: params.search || undefined,
            // `undefined` y no `false`: el filtro que no aplica no viaja en la URL.
            sin_correo: params.filtro === 'sin_correo' || undefined,
            sin_verificar: params.filtro === 'sin_verificar' || undefined,
            sin_acceso: params.filtro === 'sin_acceso' || undefined,
          },
        })
      ).data,
    enabled,
    placeholderData: (previous) => previous,
  })
}

/** Cuelga de `/{user_id}/correo`: un PATCH paramétrico en `/{user_id}` chocaría con el literal
 * `lookup` (check `colision_de_rutas`). */
export function useCorregirCorreo() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({userId, email}: {userId: string; email: string}) =>
      (await api.patch<Usuario>(`/admin/users/${userId}/correo`, {email})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: KEY}),
    // El diálogo pinta todos sus fallos; sin esto la red global (`lib/red-errores-mutacion.ts`)
    // sacaría otra burbuja de error encima.
    meta: {manejaError: true},
  })
}

export type RolMembresia = components['schemas']['EmployeeRole']

/** Enum CERRADO en ALL_CAPS: `doctrina_de_ui` lee esta convención para permitir un
 * `<select>` nativo sin caer en R3. */
export const ROLES_MEMBRESIA: readonly RolMembresia[] = ['owner', 'responsable', 'user']

/** El 409 de cupo del plan. Vive aquí y no en `lib/errors.ts` porque nunca se imprime: es la
 * señal para pedir confirmación y reintentar con `confirmar_sobre_cupo: true`. */
export const MEMBRESIA_SOBRE_CUPO = 'this membership puts the business over its plan quota'

/** openapi-typescript marca como requerido todo campo con default; `confirmar_sobre_cupo` solo
 * viaja en el reintento tras el 409. */
export type MembresiaNueva = Omit<components['schemas']['AdminMembresiaCreate'], 'confirmar_sobre_cupo'> & {
  confirmar_sobre_cupo?: boolean
}

/** Tipo y número van juntos: el número se valida contra su tipo. */
export function useCorregirDocumento() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      userId,
      tipo_documento,
      cedula,
    }: {userId: string} & components['schemas']['AdminUserDocumentoUpdate']) =>
      (await api.patch<Usuario>(`/admin/users/${userId}/documento`, {tipo_documento, cedula})).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: KEY}),
    meta: {manejaError: true},
  })
}

export function useAgregarMembresia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({userId, ...body}: {userId: string} & MembresiaNueva) =>
      (await api.post<Usuario>(`/admin/users/${userId}/membresias`, body)).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: KEY}),
    meta: {manejaError: true},
  })
}

/** Desactivar es reversible a propósito: la membresía arrastra ficha, entrevista y evidencias.
 * `confirmar_sobre_cupo` solo viaja al reintentar reactivar. */
export function useCambiarEstadoMembresia() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      userId,
      employeeId,
      verbo,
      confirmar_sobre_cupo,
    }: {
      userId: string
      employeeId: string
      verbo: 'desactivar' | 'reactivar'
      confirmar_sobre_cupo?: boolean
    }) =>
      (
        await api.post<Usuario>(`/admin/users/${userId}/membresias/${employeeId}/${verbo}`, {
          confirmar_sobre_cupo,
        })
      ).data,
    onSuccess: () => queryClient.invalidateQueries({queryKey: KEY}),
    meta: {manejaError: true},
  })
}
